import { defineAsyncComponent } from 'vue'
import type { App, DefineComponent } from 'vue'

// ==========================================
// 类型定义
// ==========================================

/**
 * 懒加载组件配置
 */
interface LazyComponentConfig {
  /** 加载函数 */
  loader: () => Promise<DefineComponent>
  /** 加载中组件 */
  loadingComponent?: DefineComponent
  /** 错误组件 */
  errorComponent?: DefineComponent
  /** 延迟时间（毫秒） */
  delay?: number
  /** 超时时间（毫秒） */
  timeout?: number
}

// ==========================================
// 懒加载组件工厂
// ==========================================
export function createLazyComponent(config: LazyComponentConfig) {
  return defineAsyncComponent({
    loader: config.loader,
    loadingComponent: config.loadingComponent,
    errorComponent: config.errorComponent,
    delay: config.delay ?? 200,
    timeout: config.timeout ?? 3000
  })
}

// 简化创建：通过路径生成懒加载组件
function lazy(path: string): DefineComponent {
  return createLazyComponent({ loader: () => import(/* @vite-ignore */ path) })
}

/**
 * 预定义的懒加载组件
 */
export const LazyComponents = {
  // 编辑器相关组件
  MFlowEditor: lazy('@/components/editor/MFlowEditor.vue'),

  // 容器
  FlowComponent: lazy('@/components/flow/container.vue'),

  // 节点组件
  ResourceGetComponent: lazy('@/components/nodes/resources/resourceGet/component.vue'),

  VariableGetComponent: lazy('@/components/nodes/variables/variableGet/component.vue'),
  VariableSetComponent: lazy('@/components/nodes/variables/variableSet/component.vue'),

  ArrayInputComponent: lazy('@/components/nodes/inputs/components/ArrayInput.vue'),
  FormInputComponent: lazy('@/components/nodes/inputs/components/FormInput.vue'),
  JsonInputComponent: lazy('@/components/nodes/inputs/components/JsonInput.vue'),

  InitialNodeComponent: lazy('@/components/nodes/triggers/components/InitialNode.vue'),
  TimerTriggerComponent: lazy('@/components/nodes/triggers/components/TimerTrigger.vue'),

  CloneDeepComponent: lazy('@/components/nodes/processing/components/CloneDeep.vue'),
  CustomFunctionComponent: lazy('@/components/nodes/processing/components/CustomFunction.vue'),
  DebugNodeComponent: lazy('@/components/nodes/processing/components/DebugNode.vue'),
  FilterComponent: lazy('@/components/nodes/processing/components/Filter.vue'),
  FindComponent: lazy('@/components/nodes/processing/components/Find.vue'),
  ForEachComponent: lazy('@/components/nodes/processing/components/ForEach.vue'),
  IfElseComponent: lazy('@/components/nodes/processing/components/IfElse.vue'),
  IsEmptyComponent: lazy('@/components/nodes/processing/components/IsEmpty.vue'),
  SwitchComponent: lazy('@/components/nodes/processing/components/Switch.vue'),

  FetchRequestComponent: lazy('@/components/nodes/http/fetchRequest/component.vue'),
  HttpRequestComponent: lazy('@/components/nodes/http/httpRequest/component.vue'),
  WebSocketClientComponent: lazy('@/components/nodes/http/webSocketClient/component.vue'),
} as Record<string, DefineComponent>

/**
 * 通过 import.meta.glob 建立 PNG 资源映射
 * 说明：Vite 不支持对静态资源使用完全动态的 import(`path/${name}.png`)，
 * 使用 glob 建立可枚举的模块映射，再按文件名后缀匹配。
 */
const pngIconModules = import.meta.glob('../assets/icon/*.png') as Record<string, () => Promise<{ default: string }>>

function resolvePngByName(name: string): (() => Promise<{ default: string }>) | null {
  const candidates = [
    `${name}.png`,
    `${name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}.png`,
    `${name.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '')}.png`
  ]
  for (const [path, loader] of Object.entries(pngIconModules)) {
    const file = path.split('/').pop() || ''
    if (candidates.includes(file)) return loader
  }
  return null
}

function buildIconCandidates(iconName: string): string[] {
  const candidates: string[] = []
  const raw = iconName
  const dashed = raw.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
  const camel = dashed
    .split('-')
    .map((p, i) => (i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1)))
    .join('')
  const add = (k: string) => { if (!candidates.includes(k)) candidates.push(k) }
  add(raw)
  add(`${raw}Icon`)
  add(dashed)
  add(`${dashed}Icon`)
  add(camel)
  add(`${camel}Icon`)
  return candidates
}

/**
 * 动态导入工具函数
 */
// ==========================================
// 动态导入工具函数
// ==========================================
export async function importIcon(iconName: string): Promise<string | null>
export async function importIcon(iconName: string, type: 'element'): Promise<DefineComponent | null>
export async function importIcon(iconName: string, type: 'png' | 'element' = 'png') {
    if (type === 'png') {
      try {
        // 规范化候选名集合（用于从 exports/icons 中兜底匹配）
        const candidatesFromExports = buildIconCandidates(iconName)

        // 1) 通过 import.meta.glob 的映射加载（与文件名精确匹配优先）
        const loader = resolvePngByName(iconName)
        if (loader) {
          const mod = await loader()
          return mod.default
        }

        // 2) 回退：尝试从统一导出表按多种命名获取
        const icons = await import('@/exports/icons')
        for (const key of candidatesFromExports) {
          const hit = (icons as any)[key]
          if (hit) return hit
        }

        // 3) 最后尝试严格按原文件名约定（纯小写）
        const lowerLoader = resolvePngByName(iconName.toLowerCase())
        if (lowerLoader) {
          const mod = await lowerLoader()
          return mod.default
        }

        console.warn(`[LazyIcon] PNG icon not found: ${iconName}`)
        return null
      } catch (error) {
        console.warn(`Failed to import PNG icon: ${iconName}`, error)
        return null
      }
    } else if (type === 'element') {
      try {
        const module = await import('@element-plus/icons-vue')
        return (module as any)[iconName]
      } catch (error) {
        console.warn(`Failed to import Element icon: ${iconName}`, error)
        return null
      }
    }
}

export async function importComponent(componentPath: string): Promise<DefineComponent | null> {
    try {
      const module = await import(/* @vite-ignore */ componentPath)
      return module.default
    } catch (error) {
      console.warn(`Failed to import component: ${componentPath}`, error)
      return null
    }
}

export async function importUtil(utilPath: string): Promise<any | null> {
    try {
      const module = await import(/* @vite-ignore */ utilPath)
      return module
    } catch (error) {
      console.warn(`Failed to import util: ${utilPath}`, error)
      return null
    }
}

export const dynamicImports = { importIcon, importComponent, importUtil }

/**
 * 批量注册懒加载组件
 */
export function registerLazyComponents(app: App) {
  Object.entries(LazyComponents).forEach(([name, component]) => {
    app.component(name, component)
  })
}

//
// 动态资源预加载器
//
/**
 * 资源预加载器
 */
export class ResourcePreloader {
  private static instance: ResourcePreloader
  private preloadedResources = new Set<string>()
  private preloadPromises = new Map<string, Promise<any>>()
  
  static getInstance() {
    if (!ResourcePreloader.instance) {
      ResourcePreloader.instance = new ResourcePreloader()
    }
    return ResourcePreloader.instance
  }
  
  /**
   * 预加载资源
   */
  async preload(resourcePath: string, type: 'component' | 'icon' | 'util' = 'component') {
    if (this.preloadedResources.has(resourcePath)) {
      return this.preloadPromises.get(resourcePath)
    }
    
    let promise: Promise<any>
    
    switch (type) {
      case 'component':
        promise = dynamicImports.importComponent(resourcePath)
        break
      case 'icon':
        promise = dynamicImports.importIcon(resourcePath)
        break
      case 'util':
        promise = dynamicImports.importUtil(resourcePath)
        break
      default:
        promise = Promise.resolve(null)
    }
    
    this.preloadPromises.set(resourcePath, promise)
    this.preloadedResources.add(resourcePath)
    
    return promise
  }
  
  /**
   * 批量预加载
   */
  async preloadBatch(resources: Array<{ path: string; type?: 'component' | 'icon' | 'util' }>) {
    const promises = resources.map(({ path, type }) => this.preload(path, type))
    return Promise.allSettled(promises)
  }
  
  /**
   * 清理预加载缓存
   */
  clear() {
    this.preloadedResources.clear()
    this.preloadPromises.clear()
  }
}

// 导出单例
export const preloader = ResourcePreloader.getInstance()